import { RankableAttribute } from '../../utils/attributeMeasure';
import { sortProductsByMeasure } from '../../utils/productSorting';
import {
  createMeasureAccessor,
  createTieBreakAccessor,
  RankableOffers,
  RankablePremium,
} from './resolveMeasureValue';
import { ProductSortOption } from './types';

/**
 * The one entry point the comparison table and the product cards both use.
 * Always derives from the incoming list, never from a previous sort, so
 * returning to the default restores the editor's order exactly.
 *
 * @param products   Products in the ProductMetaConfig order, i.e. "Popular".
 * @param getPremium Resolves a product to its quoted premiums. TI's price and
 *   discount options rank on these; a caller with no quote omits it and those
 *   two options leave the order untouched.
 * @param getOffers Resolves a product to its exclusive offers, which is where
 *   credit card's gift-value option ranks from. Omitted by a caller whose
 *   products carry none, on the same terms as `getPremium`.
 */
export function applyProductSort<T>(
  products: readonly T[],
  option: ProductSortOption | undefined,
  getAttributes: (
    product: T,
  ) => readonly RankableAttribute[] | null | undefined,
  getPremium?: (product: T) => RankablePremium | null | undefined,
  getOffers?: (product: T) => RankableOffers | null | undefined,
): T[] {
  const accessor = createMeasureAccessor(
    option,
    getAttributes,
    getPremium,
    getOffers,
  );

  if (!option) return [...products];

  // A measure that ranks on the authored order has nothing to compare, so its
  // direction can only mean "as authored" or "reversed" (§ 8a).
  if (option.rankBy === 'AUTHORED_ORDER') {
    return option.direction === 'desc'
      ? [...products].reverse()
      : [...products];
  }

  // Any other measure whose source this caller cannot supply — a premium on
  // a page with no quote, an offer list a table does not hold — ranks on
  // nothing and leaves the order exactly as it arrived. Reversing it would
  // turn a descending option the page cannot answer into a visible reshuffle.
  if (!accessor) return [...products];

  return sortProductsByMeasure(
    products,
    accessor,
    option.direction,
    createTieBreakAccessor(option, getPremium),
  );
}
